/**
 * Compass heading helpers shared by navigation services and debug UI.
 * Prefer absolute orientation (north-referenced) over relative Game Rotation Vector events.
 */

/** DeviceOrientationEvent fields used for compass heading extraction. */
export type CompassOrientationEvent = DeviceOrientationEvent & {
    "webkitCompassHeading"?:  number;
    "webkitCompassAccuracy"?: number;
};

/** Degrees-to-radians factor used by the W3C tilt-compensated heading formula. */
const DEG_TO_RAD = Math.PI / 180;

/**
 * Enter tilt-compensated formula above this tilt magnitude (degrees).
 * Higher than the leave threshold to avoid flat↔tilt snaps near the boundary.
 */
const FLAT_ENTER_TILT_DEGREES = 22;

/** Return to flat alpha heading below this tilt magnitude (degrees). */
const TILT_ENTER_FLAT_DEGREES = 10;

/**
 * Normalize an angle in degrees to the half-open interval [0, 360).
 * @param angle - Angle in degrees (any finite value).
 * @returns Normalized angle in [0, 360).
 */
export function normalizeCompassAngle( angle: number ): number {
    return ( ( angle % 360 ) + 360 ) % 360;
}

/**
 * Shortest signed angular delta from one heading to another.
 * @param fromDegrees - Starting heading in degrees.
 * @param toDegrees - Target heading in degrees.
 * @returns Signed delta in (-180, 180].
 */
export function shortestHeadingDeltaDegrees( fromDegrees: number, toDegrees: number ): number {
    return ( toDegrees - fromDegrees + 540 ) % 360 - 180;
}

/**
 * Read the current screen orientation angle used to correct deviceorientation heading.
 * @returns Screen orientation in degrees, or 0 when unavailable.
 */
export function getScreenOrientationDegrees(): number {
    try {
        const angleFromApi = window.screen?.orientation?.angle;
        if ( typeof angleFromApi === "number" && !Number.isNaN( angleFromApi ) ) {
            return normalizeCompassAngle( angleFromApi );
        }

        const legacyOrientation = ( window as unknown as { "orientation"?: number } ).orientation;
        if ( typeof legacyOrientation === "number" && !Number.isNaN( legacyOrientation ) ) {
            return normalizeCompassAngle( legacyOrientation );
        }
    } catch {
        // Ignore unavailable screen orientation APIs.
    }

    return 0;
}

/**
 * Whether the browser exposes the absolute device orientation event (Firefox / modern Chromium).
 * @returns True when `deviceorientationabsolute` can be registered.
 */
export function supportsAbsoluteDeviceOrientationEvent(): boolean {
    return typeof window !== "undefined" && "ondeviceorientationabsolute" in window;
}

/**
 * W3C Device Orientation tilt-compensated compass heading from absolute Euler angles.
 * Works when the device is upright or tilted; avoids freezing on alpha alone when β≈90°.
 * @param alphaDegrees - Z-axis rotation in degrees.
 * @param betaDegrees - X-axis rotation in degrees.
 * @param gammaDegrees - Y-axis rotation in degrees.
 * @returns Compass heading in degrees [0, 360).
 * @see https://www.w3.org/TR/orientation-event/#worked-example-2
 */
export function tiltCompensatedCompassHeading(
    alphaDegrees: number,
    betaDegrees: number,
    gammaDegrees: number,
): number {
    const betaRad = betaDegrees * DEG_TO_RAD;
    const gammaRad = gammaDegrees * DEG_TO_RAD;
    const alphaRad = alphaDegrees * DEG_TO_RAD;

    const cosGamma = Math.cos( gammaRad );
    const cosAlpha = Math.cos( alphaRad );
    const sinBeta = Math.sin( betaRad );
    const sinGamma = Math.sin( gammaRad );
    const sinAlpha = Math.sin( alphaRad );

    // World-frame horizontal components of the device -Z axis (W3C worked example).
    const vectorX = -cosAlpha * sinGamma - sinAlpha * sinBeta * cosGamma;
    const vectorY = -sinAlpha * sinGamma + cosAlpha * sinBeta * cosGamma;

    let headingRad = Math.atan( vectorX / vectorY );
    if ( vectorY < 0 ) {
        headingRad += Math.PI;
    } else if ( vectorX < 0 ) {
        headingRad += 2 * Math.PI;
    }

    return normalizeCompassAngle( headingRad * ( 180 / Math.PI ) );
}

/**
 * Result of extracting compass headings from one orientation event.
 * Exposes both flat and tilt candidates so callers can diagnose formula snaps.
 */
export type CompassHeadingExtraction = {

    /** Chosen heading after formula selection, or null when unusable. */
    "heading": number | null;

    /** Which formula produced {@link heading}. */
    "formula": "webkit" | "flat" | "tilt" | "relative" | "none";

    /** Flat-device candidate: alpha − screen orientation (null when alpha missing). */
    "flatHeading": number | null;

    /** W3C tilt-compensated candidate (null when alpha missing). */
    "tiltHeading": number | null;

    /** Max(|beta|, |gamma|) in degrees. */
    "tiltMagnitude": number;

    /** Whether the extractor is currently in flat formula mode (after hysteresis). */
    "usingFlatFormula": boolean;
};

/**
 * Extract north-referenced heading candidates from an orientation event.
 * @param event - Device orientation event (absolute or relative).
 * @param options - Extraction options.
 * @param options.allowRelativeFallback - When true, accept non-absolute alpha.
 * @param options.usingFlatFormula - Previous flat/tilt mode for hysteresis (mutated via return).
 * @returns Extraction details including both formula candidates.
 */
export function extractAbsoluteCompassHeadingDetailed(
    event: CompassOrientationEvent,
    options: {
        "allowRelativeFallback"?: boolean;
        "usingFlatFormula"?:      boolean;
    } = {},
): CompassHeadingExtraction {
    const allowRelativeFallback = options.allowRelativeFallback === true;
    let usingFlatFormula = options.usingFlatFormula !== false;

    if ( typeof event.webkitCompassHeading === "number" && !Number.isNaN( event.webkitCompassHeading ) ) {
        /*
         * iOS webkitCompassHeading increases counterclockwise — convert to the
         * clockwise bearing convention shared by the alpha/tilt paths below.
         */
        const heading = normalizeCompassAngle( 360 - event.webkitCompassHeading );

        return {
            heading,
            "formula":          "webkit",
            "flatHeading":      heading,
            "tiltHeading":      heading,
            "tiltMagnitude":    0,
            "usingFlatFormula": true,
        };
    }

    if ( typeof event.alpha !== "number" || Number.isNaN( event.alpha ) ) {
        return {
            "heading":          null,
            "formula":          "none",
            "flatHeading":      null,
            "tiltHeading":      null,
            "tiltMagnitude":    0,
            "usingFlatFormula": usingFlatFormula,
        };
    }

    const isAbsolute = event.absolute === true;
    if ( !isAbsolute && !allowRelativeFallback ) {
        return {
            "heading":          null,
            "formula":          "none",
            "flatHeading":      null,
            "tiltHeading":      null,
            "tiltMagnitude":    0,
            "usingFlatFormula": usingFlatFormula,
        };
    }

    const beta = typeof event.beta === "number" && !Number.isNaN( event.beta ) ? event.beta : 0;
    const gamma = typeof event.gamma === "number" && !Number.isNaN( event.gamma ) ? event.gamma : 0;
    const screenOrientation = getScreenOrientationDegrees();
    const tiltMagnitude = Math.max( Math.abs( beta ), Math.abs( gamma ) );

    const flatHeading = normalizeCompassAngle( event.alpha - screenOrientation );
    const tiltHeading = normalizeCompassAngle( 360 - tiltCompensatedCompassHeading( event.alpha, beta, gamma ) - screenOrientation );

    /*
     * Hysteresis: crossing a single 15° threshold snapped flat↔tilt by 40–80° in raw logs
     * (alpha stayed continuous while extracted jumped). Stay in the current mode until the
     * opposite band is clearly reached.
     */
    if ( usingFlatFormula && tiltMagnitude >= FLAT_ENTER_TILT_DEGREES ) {
        usingFlatFormula = false;
    } else if ( !usingFlatFormula && tiltMagnitude <= TILT_ENTER_FLAT_DEGREES ) {
        usingFlatFormula = true;
    }

    if ( !isAbsolute && allowRelativeFallback ) {
        return {
            "heading": flatHeading,
            "formula": "relative",
            flatHeading,
            tiltHeading,
            tiltMagnitude,
            usingFlatFormula,
        };
    }

    return {
        "heading": usingFlatFormula ? flatHeading : tiltHeading,
        "formula": usingFlatFormula ? "flat" : "tilt",
        flatHeading,
        tiltHeading,
        tiltMagnitude,
        usingFlatFormula,
    };
}

/**
 * Extract a north-referenced compass heading from an orientation event.
 * Relative Game Rotation Vector events (Firefox default `deviceorientation`) are ignored
 * unless {@link allowRelativeFallback} is true.
 * @param event - Device orientation event (absolute or relative).
 * @param options - Extraction options.
 * @param options.allowRelativeFallback - When true, accept non-absolute alpha (may drift / jump).
 * @param options.usingFlatFormula - Previous flat/tilt mode for hysteresis.
 * @returns Heading in degrees [0, 360), or null when no usable absolute heading is present.
 */
export function extractAbsoluteCompassHeading(
    event: CompassOrientationEvent,
    options: {
        "allowRelativeFallback"?: boolean;
        "usingFlatFormula"?:      boolean;
    } = {},
): number | null {
    return extractAbsoluteCompassHeadingDetailed( event, options ).heading;
}

/**
 * Heading discontinuity filter (currently a passthrough).
 *
 * Absolute samples are already stabilized by flat/tilt hysteresis and prediction
 * soft-follow. Singularity gating can be reintroduced here if a browser starts
 * emitting large Euler jumps again.
 */
export class CompassHeadingJumpFilter {

    /**
     * Create a passthrough heading filter.
     */
    public constructor() {
    }

    /**
     * Return the normalized heading unchanged.
     * @param rawHeading - Candidate heading in degrees.
     * @returns Normalized heading in [0, 360).
     */
    public filter( rawHeading: number ): number {
        return normalizeCompassAngle( rawHeading );
    }

    /**
     * Clear filter state (no-op while the filter is a passthrough).
     */
    public reset(): void {
        // No mutable state while passthrough.
    }

}
